import { css } from '@emotion/react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import Button from 'components/field/Button';
import TextArea from 'components/field/TextArea';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Checkbox from 'components/global/Checkbox';
import Typography from 'components/global/Typography';
import { ModalActions } from 'components/global/styled/Modal.styled';

import useAlert from 'hooks/useAlert';
import useAppTracking from 'hooks/useAppTracking';
import useAxiosPrivate from 'hooks/useAxiosPrivate';

import { ONBOARDING } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { languages } from 'utils/localization';

import * as S from '../SubscriptonAccountModal.styled';

/**
 * @author @muhammedburakak
 */

export default function ContactUsModal({ closeModal, prevStep, selectedPackage }) {
	// Hooks and states declaration
	const { t } = useTranslation();
	const axiosPrivate = useAxiosPrivate();
	const { updateCompany } = useAppTracking();
	const { showError, showSuccess } = useAlert();

	const qualificationJSON = useSelector((state) => state?.instruction.qualificationJSON);

	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					contactUsComment: yup.string().nullable()
				})
				.required(),
		[]
	);
	const formMethods = useForm({
		defaultValues: {
			contactUsComment: null
		},
		resolver: yupResolver(schema)
	});

	const { handleSubmit, watch, setValue } = formMethods;

	const onSubmit = (data) => {
		if (data.contactUsComment === null && Object.keys(data).length === 1) {
			showError({
				header: 'error.contactUsPleaseSelectHeader',
				message: 'error.contactUsPleaseSelectMessage'
			});
		} else {
			axiosPrivate
				.post(ONBOARDING.UPDATE_ONBOARDING, {
					qualificationJSON: JSON.stringify({
						...qualificationJSON,
						enterpriseRequest: data
					})
				})
				.then((response) => {
					if (response.status === 200) {
						showSuccess({
							header: 'success.contactUsHeader',
							message: 'success.contactUsMessage'
						});

						closeModal();
					} else {
						AppLogger.error(response);
					}
				})
				.catch((error) => {
					AppLogger.error(error);
					showError();
				});

			updateCompany({
				enterpriseRequest: Object.keys(data).map((key) => {
					if (key === 'contactUsComment') {
						return data[key];
					}
					return key;
				})
			});
		}
	};

	return (
		<>
			<S.ModalContentWrapper>
				<Form borderless formMethods={formMethods}>
					<FormItem>
						<Typography fontWeight="500" className="mb-2" noWrap>
							contactUs.title
						</Typography>

						<S.CardContainer>
							<ul>
								{selectedPackage.properties.map((item) => (
									<li key={item} style={{ display: 'flex' }}>
										<Checkbox
											name={t(item, languages[0].languageCode)}
											onClick={() => {
												setValue(t(item, languages[0].languageCode), !watch(item));
											}}
										/>
										<Typography color="var(--seconderHeader)" className="px-3" noWrap>
											{item}
										</Typography>
									</li>
								))}
							</ul>
						</S.CardContainer>
					</FormItem>
					<FormItem label="comment">
						<TextArea name="contactUsComment" />
					</FormItem>
				</Form>
			</S.ModalContentWrapper>

			<ModalActions className="subscription-modal-actions">
				<Button
					variant="blackOutline"
					css={css`
						text-decoration: underline;
						border: none !important;
					`}
					label="cancel"
					onClick={closeModal}
				/>
				<Button
					variant="outline"
					label="previous"
					onClick={() => {
						prevStep();
					}}
				/>

				<Button
					variant="blue"
					label="done"
					style={{ minWidth: '87px' }}
					onClick={handleSubmit(onSubmit, (errors) => {
						AppLogger.log(errors);
					})}
				/>
			</ModalActions>
		</>
	);
}
